import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import AuthContainer from './components/Auth/AuthContainer';
import Home from './pages/Home';
import LegacyQuorumDashboard from './pages/LegacyQuorumDashboard';
import LegacyQuorumFilmDetail from './pages/LegacyQuorumFilmDetail';
import MovieDetail from './pages/MovieDetail';
import UserManagement from './pages/admin/UserManagement';
import AgentsLoglineAnalysis from './pages/AgentsLoglineAnalysis';
import FilmSubscriptionManagement from './pages/FilmSubscriptionManagement';
import FilmSubscriptions from './pages/admin/FilmSubscriptions';
import EnterpriseCompanies from './pages/admin/EnterpriseCompanies';
import EnterpriseCompanyDetail from './pages/admin/EnterpriseCompanyDetail';
import EnterpriseCompanyDashboard from './pages/enterprise/EnterpriseCompanyDashboard';
import EnterpriseCompanyUsers from './pages/enterprise/EnterpriseCompanyUsers';
import EnterpriseAdminPanel from './pages/enterprise/EnterpriseAdminPanel';
import ProfilePage from './pages/Profile/ProfilePage';
import FilmAdminBoxOfficeProjections from './pages/FilmAdmin/BoxOfficeProjections';
import FilmAdminComps from './pages/FilmAdmin/Comps';

// Import modules
import { FilmTracking, FilmDetail } from './modules/FilmTracking';
import FilmDeepDiveList from './modules/FilmTracking/pages/FilmDeepDiveList';
import FilmDeepDiveDetail from './modules/FilmTracking/pages/FilmDeepDiveDetail';
import { CircularProgress, Box } from '@mui/material';
import { useFilmSubscription } from './hooks/useFilmSubscription';
import { FILM_TIERS } from './lib/filmSubscriptionTiers';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading, userProfile } = useAuth();

  if (loading || (currentUser && !userProfile)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// Admin Protected Route Component
const AdminRoute = ({ children }) => {
  const { currentUser, loading, userProfile, isAdmin } = useAuth();

  if (loading || (currentUser && !userProfile)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user is admin (includes admin and super_admin roles)
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Enterprise Admin Protected Route Component
const EnterpriseAdminRoute = ({ children }) => {
  const { currentUser, loading, userProfile, isFilmEnterpriseAdmin } = useAuth();

  if (loading || (currentUser && !userProfile)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user is enterprise admin
  if (!isFilmEnterpriseAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Film Admin Protected Route Component
const FilmAdminRoute = ({ children }) => {
  const { currentUser, loading, userProfile, isFilmAdmin } = useAuth();

  if (loading || (currentUser && !userProfile)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user is film admin
  if (!isFilmAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/auth" element={<AuthContainer />} />
      
      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Home />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Film Tracking Routes */}
      <Route
        path="/film-tracking"
        element={
          <ProtectedRoute>
            <Layout>
              <FilmTracking />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/film-tracking/deep-dive"
        element={
          <ProtectedRoute>
            <Layout>
              <FilmDeepDiveList />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/film-tracking/deep-dive/:ttId"
        element={
          <ProtectedRoute>
            <Layout>
              <FilmDeepDiveDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/film-tracking/:ttId"
        element={
          <ProtectedRoute>
            <Layout>
              <FilmDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Agents Routes */}
      <Route
        path="/agents/logline-analysis"
        element={
          <ProtectedRoute>
            <Layout>
              <AgentsLoglineAnalysis />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/release-calendar"
        element={
          <ProtectedRoute>
            <Layout>
              <Box sx={{ p: 3 }}>
                <h1>Release Calendar</h1>
                <p>Release calendar module coming soon...</p>
              </Box>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/comp-finder"
        element={
          <ProtectedRoute>
            <Layout>
              <Box sx={{ p: 3 }}>
                <h1>Comp Finder</h1>
                <p>Comp finder module coming soon...</p>
              </Box>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes - Specific routes first */}
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <Layout>
              <UserManagement />
            </Layout>
          </AdminRoute>
        }
      />
      {/* Film Subscription Management - User-facing */}
      <Route
        path="/subscription/film"
        element={
          <ProtectedRoute>
            <Layout>
              <FilmSubscriptionManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes - Film Subscriptions */}
      <Route
        path="/admin/film-subscriptions"
        element={
          <AdminRoute>
            <Layout>
              <FilmSubscriptions />
            </Layout>
          </AdminRoute>
        }
      />

      {/* Admin Routes - Film Companies */}
      <Route
        path="/admin/enterprise-companies"
        element={
          <AdminRoute>
            <Layout>
              <EnterpriseCompanies />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/enterprise-companies/:companyId"
        element={
          <AdminRoute>
            <Layout>
              <EnterpriseCompanyDetail />
            </Layout>
          </AdminRoute>
        }
      />
      <Route path="/admin/film-companies" element={<Navigate to="/admin/enterprise-companies" replace />} />

      {/* Enterprise Admin Routes */}
      <Route
        path="/enterprise/admin"
        element={
          <EnterpriseAdminRoute>
            <Layout>
              <EnterpriseAdminPanel />
            </Layout>
          </EnterpriseAdminRoute>
        }
      />
      <Route
        path="/enterprise/company/dashboard"
        element={
          <EnterpriseAdminRoute>
            <Layout>
              <EnterpriseCompanyDashboard />
            </Layout>
          </EnterpriseAdminRoute>
        }
      />
      <Route
        path="/enterprise/company/users"
        element={
          <EnterpriseAdminRoute>
            <Layout>
              <EnterpriseCompanyUsers />
            </Layout>
          </EnterpriseAdminRoute>
        }
      />
      <Route path="/enterprise/film/dashboard" element={<Navigate to="/enterprise/admin" replace />} />
      <Route path="/enterprise/film/users" element={<Navigate to="/enterprise/admin" replace />} />

      {/* Film Admin Routes */}
      <Route
        path="/film-admin/box-office-projections"
        element={
          <FilmAdminRoute>
            <Layout>
              <FilmAdminBoxOfficeProjections />
            </Layout>
          </FilmAdminRoute>
        }
      />
      <Route
        path="/film-admin/comps"
        element={
          <FilmAdminRoute>
            <Layout>
              <FilmAdminComps />
            </Layout>
          </FilmAdminRoute>
        }
      />
      
      {/* Admin catch-all route - must be last */}
      <Route
        path="/admin/*"
        element={
          <AdminRoute>
            <Layout>
              <Box sx={{ p: 3 }}>
                <h1>Admin Panel</h1>
                <p>Admin functionality coming soon...</p>
              </Box>
            </Layout>
          </AdminRoute>
        }
      />
      
      {/* Legacy Quorum Route */}
      <Route
        path="/film-tracking/legacy-quorum"
        element={
          <ProtectedRoute>
            <Layout>
              <LegacyQuorumDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/film-tracking/legacy-quorum/film/:filmId"
        element={
          <ProtectedRoute>
            <Layout>
              <LegacyQuorumFilmDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Movie Detail Route */}
      <Route
        path="/movie/:movieId"
        element={
          <ProtectedRoute>
            <Layout>
              <MovieDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;
